import { useMemo, useState } from "react";
import { useClasses } from "@api/class.api";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    EXCEL_RESERVED_SHEET_NAME,
    findReportPackageSheetNameClashes,
    isReservedReportPackageSheetName,
    toReportPackageSheetName,
} from "@common/helpers/report-package-sheet-name";
import {
    AR_AGING_REPORT_PACKAGE_REPORT_TYPES,
    isAccrualReportPackageType,
    REPORT_PACKAGE_CYCLE_SUPPORTED,
    REPORT_PACKAGE_MAX_ENTITIES,
    REPORT_PACKAGE_MAX_ROWS,
} from "@common/types/reports/report-package";
import {
    BreadcrumbItem,
    Breadcrumbs,
} from "@design-system/Breadcrumbs/Breadcrumbs";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { Drawer } from "@design-system/Drawer";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import invoiceStyles from "@components/invoices/NativeInvoiceDrawer/NativeInvoiceDrawer.module.scss";
import { exportReportPackageRows } from "@api/accountingReports.api";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import { PlusIcon } from "@icons";
import {
    BASE_REPORT_PACKAGE_REPORT_TYPES,
    REPORT_PACKAGE_ACCRUAL_UNAVAILABLE_TOOLTIP,
    resolveReportPackageLedgerId,
    canSelectAccrualBasis,
} from "../ReportPackageModal/reportPackageBasis.lib";
import { useReportPackageSettings } from "../ReportPackageModal/useReportPackageSettings";
import { ReportPackageBuilderRows } from "./ReportPackageBuilderRows";
import {
    getReportPackageRowOptionDefaults,
    getReportPackageRowEntityFilters,
    getReportPackageRowRequestDates,
    getReportPackageRowRequestFilters,
    getReportPackageRowTypeDatePatch,
    hasCompleteReportPackageRowDates,
    ReportPackageRow,
} from "./reportPackageRows.lib";
import { useReportPackageRows } from "./useReportPackageRows";
import styles from "./ReportPackageBuilderDrawer.module.scss";
import { AccountingReportType } from "@common/types/reports/report-type";
import { DEFAULT_SCHEDULES_REPORT_LAYOUT } from "../schedulesReport/useSchedulesReportParams";

interface Props {
    show: boolean;
    onHide: () => void;
    defaultEntityId?: number;
    defaultDateRange?: { start: string; end: string };
    ledgerId?: string;
}

const REPORT_PACKAGE_REPORT_TYPES = [
    ...BASE_REPORT_PACKAGE_REPORT_TYPES,
    ...AR_AGING_REPORT_PACKAGE_REPORT_TYPES,
];

export const ReportPackageBuilderDrawer = ({
    show,
    onHide,
    defaultEntityId,
    defaultDateRange,
    ledgerId,
}: Props) => {
    const {
        selectedEntityIds,
        setSelectedEntityIds,
        dateRange,
        setDateRange,
        pinnedPresets,
        onTogglePin,
        entityTriggerValue,
        availableEntityIds,
        accrualEntityIds,
        isAccrualLookupLoading,
        ledgersByEntityId,
    } = useReportPackageSettings({
        show,
        defaultEntityId,
        defaultDateRange,
        ledgerId,
    });
    const {
        rows,
        addRow,
        updateRow,
        updateRowTitle,
        removeRow,
        reorderRows,
        resetRows,
    } = useReportPackageRows();

    const { classesWithChildren } = useClasses();
    const [isEntityPerRow, setIsEntityPerRow] = useState(false);
    const defaultRowEntityId =
        selectedEntityIds[0] ??
        defaultEntityId ??
        rows.find((row) => row.entityId !== undefined)?.entityId;
    const downloadEntityIds = isEntityPerRow
        ? [
              ...new Set(
                  rows.flatMap((row) =>
                      row.entityId === undefined ? [] : [row.entityId],
                  ),
              ),
          ]
        : selectedEntityIds;
    const getRowEntityIds = (row: ReportPackageRow) => {
        if (!isEntityPerRow) {
            return selectedEntityIds;
        }
        return row.entityId === undefined ? [] : [row.entityId];
    };
    const isRowAccrualAvailable = (row: ReportPackageRow) =>
        canSelectAccrualBasis({
            selectedEntityIds: getRowEntityIds(row),
            accrualEntityIds,
            isLoading: isAccrualLookupLoading,
        });

    const rowTitleErrors = useMemo(() => {
        const clashingIndexes = new Set(
            findReportPackageSheetNameClashes(rows.map((row) => row.title)),
        );
        return new Map(
            rows.map((row, index) => {
                if (toReportPackageSheetName(row.title) === "") {
                    return [row.id, "Enter a title."];
                }
                if (isReservedReportPackageSheetName(row.title)) {
                    return [
                        row.id,
                        `Use a title other than ${EXCEL_RESERVED_SHEET_NAME}.`,
                    ];
                }
                if (clashingIndexes.has(index)) {
                    return [row.id, "Another row already has this name."];
                }
                return [row.id, undefined];
            }),
        );
    }, [rows]);

    const hasRowTitleErrors = [...rowTitleErrors.values()].some(
        (error) => error !== undefined,
    );

    const hasTooManyEntities =
        downloadEntityIds.length > REPORT_PACKAGE_MAX_ENTITIES;
    const hasUnavailableAccrualRows = rows.some(
        (row) =>
            !isRowAccrualAvailable(row) &&
            (isAccrualReportPackageType(row.reportType) ||
                row.basis === AccountingLedgerBasis.accruals),
    );

    const rowsForDownload = rows.flatMap((row) => {
        const basis = isAccrualReportPackageType(row.reportType)
            ? AccountingLedgerBasis.accruals
            : row.basis;
        if (
            basis === AccountingLedgerBasis.accruals &&
            !isRowAccrualAvailable(row)
        ) {
            return [];
        }
        const resolvedLedgerId = resolveReportPackageLedgerId({
            ledgersByEntityId,
            selectedEntityIds: getRowEntityIds(row),
            basis,
        });
        if (!resolvedLedgerId) {
            return [];
        }
        return [
            {
                entityId: isEntityPerRow ? row.entityId : undefined,
                title: row.title,
                ...(row.reportType === AccountingReportType.SCHEDULES
                    ? {
                          reportType: AccountingReportType.SCHEDULES as const,
                          layout: row.layout ?? DEFAULT_SCHEDULES_REPORT_LAYOUT,
                          cycle: row.cycle,
                          rows: row.rows,
                          revenueSubMode: row.revenueSubMode,
                          method: row.method,
                      }
                    : { reportType: row.reportType }),
                ledgerId: resolvedLedgerId,
                ...getReportPackageRowRequestDates(row),
                ...getReportPackageRowRequestFilters(row),
                ...(REPORT_PACKAGE_CYCLE_SUPPORTED.has(row.reportType)
                    ? {
                          cycle: row.cycle,
                          comparisons: row.comparisons,
                          comparisonDiffs: row.comparisonDiffs,
                          ratioTypes: row.ratioTypes,
                      }
                    : {}),
            },
        ];
    });
    const hasResolvedLedgerForEveryRow = rowsForDownload.length === rows.length;

    const { download, isDownloading } = useFileDownload(async () => {
        if (
            !dateRange.start ||
            !dateRange.end ||
            !hasResolvedLedgerForEveryRow ||
            isAccrualLookupLoading
        ) {
            return;
        }
        const response = await exportReportPackageRows({
            entityIds: downloadEntityIds,
            startDate: dateRange.start,
            endDate: dateRange.end,
            rows: rowsForDownload,
        });
        if (!response) {
            return;
        }
        saveBlobResponse(response, "Report Package");
    });

    const isDownloadEnabled =
        downloadEntityIds.length >= 1 &&
        !hasTooManyEntities &&
        rows.length > 0 &&
        rows.every(hasCompleteReportPackageRowDates) &&
        !hasRowTitleErrors &&
        dateRange.start !== undefined &&
        dateRange.end !== undefined &&
        hasResolvedLedgerForEveryRow &&
        !isAccrualLookupLoading;

    return (
        <Drawer
            open={show}
            onClose={onHide}
            onAnimationEnd={(isOpen) => {
                if (!isOpen) {
                    resetRows();
                    setIsEntityPerRow(false);
                }
            }}
            headerClassName={invoiceStyles.flatHeader}
            direction="right"
            size="fullScreen"
            noPadding
            headerAddonLeft={
                <Breadcrumbs
                    aria-label="Custom report package"
                    className={invoiceStyles.draftHeaderBreadcrumbs}
                >
                    <BreadcrumbItem current>
                        Custom report package
                    </BreadcrumbItem>
                </Breadcrumbs>
            }
        >
            <div className={invoiceStyles.workspace}>
                <div className={styles.scrollArea}>
                    <VStack gap={24} className={styles.column}>
                        <VStack gap={16}>
                            <Checkbox
                                label="Choose an entity for each report"
                                checked={isEntityPerRow}
                                onCheckedChange={(checked) => {
                                    rows.forEach((row) => {
                                        const entityId =
                                            row.entityId ?? defaultRowEntityId;
                                        updateRow(row.id, {
                                            ...(checked ? { entityId } : {}),
                                            filters:
                                                getReportPackageRowEntityFilters(
                                                    {
                                                        row,
                                                        previousEntityId:
                                                            isEntityPerRow
                                                                ? row.entityId
                                                                : selectedEntityIds[0],
                                                        entityId: checked
                                                            ? entityId
                                                            : selectedEntityIds[0],
                                                    },
                                                ),
                                        });
                                    });
                                    setIsEntityPerRow(checked);
                                }}
                            />
                            {!isEntityPerRow && (
                                <Field
                                    label="Entities"
                                    control="button"
                                    error={
                                        hasTooManyEntities
                                            ? `Select up to ${REPORT_PACKAGE_MAX_ENTITIES} entities`
                                            : undefined
                                    }
                                >
                                    <EntityFilterCombobox
                                        filters={{
                                            entityIds: selectedEntityIds,
                                        }}
                                        onChange={({ entityIds }) => {
                                            const nextEntityIds =
                                                entityIds ?? [];
                                            rows.forEach((row) => {
                                                updateRow(row.id, {
                                                    filters:
                                                        getReportPackageRowEntityFilters(
                                                            {
                                                                row,
                                                                previousEntityId:
                                                                    selectedEntityIds[0],
                                                                entityId:
                                                                    nextEntityIds[0],
                                                            },
                                                        ),
                                                });
                                            });
                                            setSelectedEntityIds(nextEntityIds);
                                        }}
                                        mode="selection"
                                        onlyBusiness
                                        excludeAllMockEntities
                                        isEntityDisabled={(entity) =>
                                            !availableEntityIds.includes(
                                                entity.id,
                                            )
                                        }
                                        renderTrigger={({ triggerProps }) => (
                                            <Button
                                                {...triggerProps}
                                                variant="primary"
                                                size="md"
                                                className={styles.triggerButton}
                                            >
                                                {entityTriggerValue ??
                                                    "Select entities"}
                                            </Button>
                                        )}
                                    />
                                </Field>
                            )}
                            <Field label="Date range" control="button">
                                <Field.Control
                                    render={(controlProps) => (
                                        <DateRangeFilterNext
                                            onChange={setDateRange}
                                            dateRange={dateRange}
                                            requireSelection
                                            pinnedPresets={pinnedPresets}
                                            onTogglePin={onTogglePin}
                                            buttonClass={styles.triggerButton}
                                            fieldControlProps={pickFieldControlAriaProps(
                                                controlProps,
                                            )}
                                        />
                                    )}
                                />
                            </Field>
                        </VStack>
                        <ReportPackageBuilderRows
                            rows={rows}
                            reportTypes={REPORT_PACKAGE_REPORT_TYPES}
                            selectedEntityIds={selectedEntityIds}
                            ledgersByEntityId={ledgersByEntityId}
                            isRowAccrualAvailable={isRowAccrualAvailable}
                            isEntityPerRow={isEntityPerRow}
                            availableEntityIds={availableEntityIds}
                            titleErrors={rowTitleErrors}
                            canGroupByClass={classesWithChildren.length > 0}
                            packageDateRange={dateRange}
                            pinnedPresets={pinnedPresets}
                            onTogglePin={onTogglePin}
                            onUpdate={updateRow}
                            onReorder={reorderRows}
                            onReportTypeChange={(row, reportType, layout) =>
                                updateRow(row.id, {
                                    reportType,
                                    ...getReportPackageRowOptionDefaults(
                                        reportType,
                                        layout,
                                    ),
                                    ...getReportPackageRowTypeDatePatch({
                                        row,
                                        reportType,
                                        packageDateRange: dateRange,
                                    }),
                                })
                            }
                            onOptionsChange={(rowId, options) =>
                                updateRow(rowId, options)
                            }
                            onBasisChange={(rowId, basis) =>
                                updateRow(rowId, { basis })
                            }
                            onTitleChange={updateRowTitle}
                            onRemove={removeRow}
                        />
                        <div>
                            <Button
                                variant="secondary"
                                size="md"
                                leftIcon={<PlusIcon />}
                                onClick={() =>
                                    addRow(
                                        isEntityPerRow
                                            ? defaultRowEntityId
                                            : undefined,
                                    )
                                }
                                disabled={
                                    rows.length >= REPORT_PACKAGE_MAX_ROWS
                                }
                            >
                                Add report
                            </Button>
                        </div>
                    </VStack>
                </div>
                <CommandIsland className={invoiceStyles.commandBar}>
                    <div className={invoiceStyles.commandBarGroup} />
                    <div className={invoiceStyles.commandBarGroup}>
                        <Button variant="secondary" size="md" onClick={onHide}>
                            Cancel
                        </Button>
                        <Tooltip
                            content={
                                hasUnavailableAccrualRows
                                    ? REPORT_PACKAGE_ACCRUAL_UNAVAILABLE_TOOLTIP
                                    : undefined
                            }
                        >
                            <ButtonWithLoader
                                variant="primary"
                                size="md"
                                loading={isDownloading}
                                disabled={!isDownloadEnabled}
                                onClick={async () => {
                                    const succeeded = await download();
                                    if (succeeded) {
                                        onHide();
                                    }
                                }}
                            >
                                Download
                            </ButtonWithLoader>
                        </Tooltip>
                    </div>
                </CommandIsland>
            </div>
        </Drawer>
    );
};
